import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../api/requests';
import log from '../../logger';

// Fetch cuisines
export const fetchCuisines = createAsyncThunk(
  'cuisine/fetchCuisines',
  async ({ page = 1, limit = 10, filters = {} } = {}, { rejectWithValue }) => {
    try {
      log.info('Fetching cuisines with page:', page, 'and limit:', limit);
      const response = await api.cuisines.getAll({ page, limit, filters });
      if (response.data.status) {
        log.info('Cuisines fetched successfully:', response.data.data);
        return response.data.data;
      } else {
        log.warn('Failed to fetch cuisines:', response.data.message);
        return rejectWithValue(response.data.message);
      }
    } catch (error) {
      log.error('Error fetching cuisines:', error);
      return rejectWithValue(error.response?.data?.message || 'Error fetching cuisines');
    }
  }
);

// Search cuisines with keyword
export const searchCuisines = createAsyncThunk(
  'cuisine/searchCuisines',
  async ({ keyword, page = 1, limit = 10, filters = {} } = {}, { rejectWithValue }) => {
    try {
      log.info('Searching cuisines with keyword:', keyword);
      const response = await api.cuisines.search({ keyword, page, limit, ...filters });
      if (response.data.status) {
        log.info('Cuisines search completed successfully:', response.data.data);
        return response.data.data;
      } else {
        log.warn('Failed to search cuisines:', response.data.message);
        return rejectWithValue(response.data.message);
      }
    } catch (error) {
      log.error('Error searching cuisines:', error);
      return rejectWithValue(error.response?.data?.message || 'Error searching cuisines');
    }
  }
);

// Create cuisine
export const createCuisine = createAsyncThunk(
  'cuisine/createCuisine',
  async (formData, { rejectWithValue }) => {
    try {
      log.info('Creating cuisine with data:', formData);
      const response = await api.cuisines.create(formData);
      if (response.data.status) {
        log.info('Cuisine created successfully:', response.data.data);
        return response.data.data;
      } else {
        log.warn('Failed to create cuisine:', response.data.message);
        return rejectWithValue(response.data.message);
      }
    } catch (error) {
      log.error('Error creating cuisine:', error);
      return rejectWithValue(error.response?.data?.message || 'Failed to create cuisine');
    }
  }
);

// Update cuisine
export const updateCuisine = createAsyncThunk(
  'cuisine/updateCuisine',
  async ({ id, formData }, { rejectWithValue }) => {
    try {
      log.info('Updating cuisine with ID:', id, 'and data:', formData);
      const response = await api.cuisines.update(id, formData);
      if (response.data.status) {
        log.info('Cuisine updated successfully:', response.data.data);
        return response.data.data;
      } else {
        log.warn('Failed to update cuisine:', response.data.message);
        return rejectWithValue(response.data.message);
      }
    } catch (error) {
      log.error('Error updating cuisine:', error);
      return rejectWithValue(error.response?.data?.message || 'Error updating cuisine');
    }
  }
);

// Delete cuisine
export const deleteCuisine = createAsyncThunk(
  'cuisine/deleteCuisine',
  async (id, { rejectWithValue }) => {
    try {
      log.info('Deleting cuisine with ID:', id);
      const response = await api.cuisines.delete(id);
      if (response.data.status) {
        log.info('Cuisine deleted successfully:', id);
        return id;
      } else {
        log.warn('Failed to delete cuisine:', response.data.message);
        return rejectWithValue(response.data.message);
      }
    } catch (error) {
      log.error('Error deleting cuisine:', error);
      return rejectWithValue(error.response?.data?.message || 'Error deleting cuisine');
    }
  }
);

// Bulk delete cuisines
export const bulkDeleteCuisines = createAsyncThunk(
  'cuisine/bulkDeleteCuisines',
  async (ids, { rejectWithValue }) => {
    try {
      await Promise.all(ids.map(id => api.cuisines.delete(id)));
      return ids;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Error deleting cuisines');
    }
  }
);

// Bulk activate/deactivate cuisines
export const bulkUpdateCuisineStatus = createAsyncThunk(
  'cuisine/bulkUpdateStatus',
  async ({ ids, status }, { rejectWithValue }) => {
    try {
      await api.cuisines.bulkUpdateStatus(ids, status);
      return { ids, status };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Error updating cuisine status');
    }
  }
);

// Fetch a single cuisine by ID
export const fetchCuisineById = createAsyncThunk(
  'cuisine/fetchCuisineById',
  async (id, { rejectWithValue }) => {
    try {
      log.info('Fetching cuisine with ID:', id);
      const response = await api.cuisines.getById(id);
      if (response.data.status) {
        log.info('Cuisine fetched successfully:', response.data.data);
        return response.data.data;
      } else {
        log.warn('Failed to fetch cuisine:', response.data.message);
        return rejectWithValue(response.data.message);
      }
    } catch (error) {
      log.error('Error fetching cuisine:', error);
      return rejectWithValue(error.response?.data?.message || 'Error fetching cuisine');
    }
  }
);

export const updateCuisineOrders = createAsyncThunk(
  'cuisine/updateOrders',
  async (cuisines) => {
    try {
      const response = await api.cuisines.updateOrders(cuisines);
      return response.data;
    } catch (error) {
      throw error.response?.data?.message || 'Failed to update cuisine orders';
    }
  }
);

const initialState = {
  items: [],
  total: 0,
  page: 1,
  pages: 0,
  limit: 10,
  loading: false,
  error: null
};

const cuisineSlice = createSlice({
  name: 'cuisine',
  initialState,
  reducers: {
    reorderCuisines: (state, action) => {
      const { oldIndex, newIndex } = action.payload;
      const [movedItem] = state.items.splice(oldIndex, 1);
      state.items.splice(newIndex, 0, movedItem);
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch cuisines
      .addCase(fetchCuisines.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCuisines.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.items = action.payload.cuisines || [];
        state.total = parseInt(action.payload.total || 0);
        state.page = parseInt(action.payload.page || 1);
        state.pages = parseInt(action.payload.pages || 0);
        state.limit = parseInt(action.payload.limit || 10);
      })
      .addCase(fetchCuisines.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Search cuisines
      .addCase(searchCuisines.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(searchCuisines.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.items = action.payload.cuisines || [];
        state.total = parseInt(action.payload.total || 0);
        state.page = parseInt(action.payload.page || 1);
        state.pages = parseInt(action.payload.pages || 0);
        state.limit = parseInt(action.payload.limit || 10);
      })
      .addCase(searchCuisines.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Create cuisine
      .addCase(createCuisine.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createCuisine.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.items.push(action.payload);
      })
      .addCase(createCuisine.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Update cuisine
      .addCase(updateCuisine.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateCuisine.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        const index = state.items.findIndex(item => item._id === action.payload._id);
        if (index !== -1) {
          state.items[index] = action.payload;
        }
      })
      .addCase(updateCuisine.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Delete cuisine
      .addCase(deleteCuisine.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteCuisine.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.items = state.items.filter(item => item._id !== action.payload);
      })
      .addCase(deleteCuisine.rejected, (state) => {
        state.loading = false;
      })
      // Bulk delete cuisines
      .addCase(bulkDeleteCuisines.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(bulkDeleteCuisines.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(bulkDeleteCuisines.rejected, (state) => {
        state.loading = false;
      })
      .addCase(bulkUpdateCuisineStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(bulkUpdateCuisineStatus.fulfilled, (state, action) => {
        state.loading = false;
        const { ids, status } = action.payload;
        state.items = state.items.map(item => ids.includes(item._id) ? { ...item, status } : item);
      })
      .addCase(bulkUpdateCuisineStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(updateCuisineOrders.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateCuisineOrders.fulfilled, (state) => {
        state.loading = false;
        state.error = null;
      })
      .addCase(updateCuisineOrders.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      });
  },
});

export default cuisineSlice.reducer; 